import { types, getEnv, flow, applyPatch, getRoot } from 'mobx-state-tree';
import { forEach, uniq, isEqual, split } from 'lodash';
import { autorun } from 'mobx';

import RequestState, { RequestStateType } from '../types/RequestState';
import Product from '../models/Product';
import { stringify } from '../util/queryString';
import ProductSearchPaginator from '../models/ProductSearchPaginator';
import { paramsToQueryIdentifier } from '../util/query';
import createMSTPatch from '../util/createMSTPatch';
import StatefulStore from '../models/StatefulStore';
import Error from '../models/Error';
import { createErrorModel } from '../util/error';
import ProductSortSelectorMap from '../types/ProductSortOrderByMap';

const LAST_VISITED_PRODUCTS_KEY = 'nh_last_visited_products';

const ProductStore = StatefulStore.named('ProductStore')
  .props({
    selectedProductColumn: types.maybeNull(types.string),
    selectedProductRow: types.maybeNull(types.string),
    productQueryResults: types.optional(types.map(ProductSearchPaginator), {}),
    productQueryStates: types.optional(types.map(RequestStateType), {}),
    products: types.optional(types.map(Product), {}),
    productStates: types.optional(types.map(RequestStateType), {}),
    productSortOptions: types.frozen(ProductSortSelectorMap),
    productVariationColumn: types.maybeNull(types.string),
    productVariationRow: types.maybeNull(types.string),
    uploadedFiles: types.optional(
      types.array(
        types.model({
          id: types.string,
          files: types.array(types.string)
        })
      ),
      []
    ),
    uploadedFilesError: types.maybeNull(Error),
    uploadedFilesState: types.optional(RequestStateType, RequestState.NONE)
  })
  .actions((self) => {
    const baseApi = (endpoint) =>
      ifShoppingCenter() ? `shopping-center/${endpoint}` : `${endpoint}`;

    const ifShoppingCenter = () =>
      getRoot(self).configStore.siteConfig.isShoppingCenter;

    const activeProductSortOptions = () =>
      split(
        getRoot(self).configStore.productList.productListSortSelectors,
        ','
      );

    const customerGroupId = () => getRoot(self).accountStore.getCustomerGroupId;

    const updateIndividualProducts = (products) => {
      products.forEach((product) => {
        let identifiersEqual = true;
        const oldProduct = self.products.get(product.id);
        if (oldProduct) {
          updateOldProduct(product, oldProduct, identifiersEqual);
        } else {
          self.products.set(product.id, product);
          self.filterByCustomerGroupVisibility(product.id);
        }
        self.productStates.set(product.id, RequestState.LOADED);
      });
    };

    const updateOldProduct = (product, oldProduct, identifiersEqual) => {
      self.filterByCustomerGroupVisibility(oldProduct.id);
      if (oldProduct.multi && product.multi && product.multi.children) {
        identifiersEqual = isEqual(
          oldProduct.multi.getChildIdentifiers(),
          product.multi.children.map((child) => child.id)
        );
      }

      if (identifiersEqual) {
        const patch = createMSTPatch(oldProduct, product);
        applyPatch(oldProduct, patch);
      }
    };

    return {
      afterCreate: () => {
        self.lastVisitedProducts = self.getLocalStorageLastVisitedIds();
        // Limit the amount of queries and products in memory.
        // "Garbage collect" maximum every 5 seconds
        autorun(
          () => {
            const queryLimit = 10;
            const productLimit = 1000;

            if (self.productQueryResults.size > queryLimit) {
              const queriesToDelete = [];

              // We use lodash forEach instead of for ... of for IE 11 compatibility
              forEach([...self.productQueryResults.keys()], (queryKey) => {
                queriesToDelete.push(queryKey);
                const queriesLeft =
                  self.productQueryResults.size - queriesToDelete.length;
                if (queriesLeft <= queryLimit) {
                  return false; //break
                }
              });

              self.deleteQueries(queriesToDelete);
            }

            if (self.products.size > productLimit) {
              const referencedProducts = {};
              self.productQueryResults.forEach((productSearchPaginator) => {
                productSearchPaginator.data.forEach((product) => {
                  referencedProducts[product.id] = 1;
                });
              });
              const productsToDelete = [];

              // We use lodash forEach instead of for ... of for IE 11 compatibility
              forEach([...self.products.keys()], (id) => {
                if (!referencedProducts[id]) {
                  productsToDelete.push(id);
                }
                const productsLeft =
                  self.products.size - productsToDelete.length;
                if (productsLeft <= productLimit) {
                  return false; //break
                }
              });
              self.deleteProducts(productsToDelete);
            }
          },
          { delay: 5000 }
        );
      },
      activeProductSortList: () => {
        const activeSortList = activeProductSortOptions();
        return activeSortList
          ? self.productSortOptions.filter(
              (ss) => activeSortList.indexOf(ss.sortIndex.toString()) > -1
            )
          : self.productSortOptions[0];
      },
      deleteQueries(keys) {
        keys.forEach((key) => {
          self.productQueryResults.delete(key);
          self.productQueryStates.delete(key);
        });
      },
      deleteProducts(ids) {
        ids.forEach((id) => {
          self.productStates.delete(id);
          self.products.delete(id);
        });
      },
      filterByCustomerGroupVisibility(productId) {
        const product = self.products.get(productId);

        if (product && product.isMulti()) {
          const children = product.multi.children.filter((childProduct) =>
            childProduct.hasCustomerGroupVisibility(customerGroupId())
          );
          product.multi.setChildren(children);
        }
      },
      getLocalStorageLastVisitedIds: () => {
        return (
          JSON.parse(window.localStorage.getItem(LAST_VISITED_PRODUCTS_KEY)) ||
          []
        );
      },
      insertLastVisitedProduct: (productId) => {
        const localStorageIds = self.getLocalStorageLastVisitedIds();
        localStorageIds.unshift(productId);
        const uniqueIds = uniq(localStorageIds).slice(0, 20);

        window.localStorage.setItem(
          LAST_VISITED_PRODUCTS_KEY,
          JSON.stringify(uniqueIds)
        );
        self.lastVisitedProducts = uniqueIds;
      },
      loadProduct: flow(function* loadProduct(id, params = null) {
        // Backend is case insensitive
        // and in case of mix of lowercase / uppercase it will return product with new id.
        let fixedId = id;
        self.setLoading(true);
        self.productStates.set(id, RequestState.LOADING);

        try {
          const product = yield getEnv(self).apiWrapper.request(
            `${baseApi('products')}/${id}?${stringify(params)}`
          );
          self.products.set(product.id, product);
          self.filterByCustomerGroupVisibility(product.id);

          if (!isEqual(product.id, id)) {
            fixedId = product.id;
          }
        } catch (e) {
          self.setError(e);
          self.productStates.set(id, RequestState.ERROR);
          throw e;
        }

        self.setLoading(false);
        self.productStates.set(fixedId, RequestState.LOADED);
        return fixedId;
      }),
      loadProducts: flow(function* loadProducts(
        searchParameters,
        allSections = false,
        overrideUrl = null
      ) {
        const queryIdentifier = paramsToQueryIdentifier(searchParameters);
        const url = overrideUrl ? overrideUrl : baseApi('products');
        // Skip the API call if we already have the data
        if (self.productQueryStates.get(queryIdentifier)) {
          return;
        }

        self.setLoading(true);
        self.productQueryStates.set(queryIdentifier, RequestState.LOADING);

        try {
          let productSearchPaginator;
          if (allSections) {
            productSearchPaginator = yield getEnv(self).apiWrapper.request(
              `${url}?${stringify(searchParameters)}`,
              {},
              { active_section: null }
            );
          } else {
            productSearchPaginator = yield getEnv(self).apiWrapper.request(
              `${url}?${stringify(searchParameters)}`
            );
          }
          const products = productSearchPaginator.data;

          // Update individual products and statuses
          updateIndividualProducts(products);

          // Set current query results
          self.productQueryResults.set(queryIdentifier, {
            ...productSearchPaginator,
            data: products.map((product) => product.id)
          });
        } catch (e) {
          self.setError(e);
          self.productQueryStates.set(queryIdentifier, RequestState.ERROR);
          throw e;
        }

        self.setLoading(false);
        self.productQueryStates.set(queryIdentifier, RequestState.LOADED);
      }),
      updateProduct: (product) => {
        self.products.set(product.id, product);
        self.filterByCustomerGroupVisibility(product.id);
        self.productStates.set(product.id, RequestState.LOADED);
      },
      loadUploadedFiles: flow(function* loadUploadedFiles() {
        self.uploadedFilesState = RequestState.LOADING;

        try {
          self.uploadedFiles = yield getEnv(self).apiWrapper.request(
            `product-file-uploads`,
            {},
            { active_section: null }
          );
        } catch (e) {
          self.uploadedFilesError = createErrorModel(e);
          self.uploadedFilesState = RequestState.ERROR;
          throw e;
        }

        self.uploadedFilesState = RequestState.LOADED;
      }),
      getProductList: () => {
        return getEnv(self)
          .apiWrapper.apiAxios()
          .get(`product-lists/products`);
      },
      getProductByAccessToken: (params) => {
        return getEnv(self)
          .apiWrapper.apiAxios()
          .get(`product-lists/products`, { params });
      },
      saveProduct: (product) => {
        return getEnv(self)
          .apiWrapper.apiAxios()
          .post(`product-lists/add-product`, product);
      },
      deleteProduct: (params) => {
        return getEnv(self)
          .apiWrapper.apiAxios()
          .delete(`product-lists/delete-product`, { params });
      },
      sendProductListEmail: (productListEmail) => {
        return getEnv(self)
          .apiWrapper.apiAxios()
          .post(`product-lists/send`, productListEmail);
      },
      setProductVariationColumn: (column) => {
        self.productVariationColumn = column;
      },
      getProductVariationColumn: () => {
        return self.productVariationColumn;
      },
      setProductVariationRow: (row) => {
        self.productVariationRow = row;
      },
      getProductVariationRow: () => {
        return self.productVariationRow;
      },
      setSelectedColumn: (column) => {
        self.selectedProductColumn = column;
      },
      setSelectedRow: (row) => {
        self.selectedProductRow = row;
      }
    };
  });

export default ProductStore;



// WEBPACK FOOTER //
// ./src/store/ProductStore.js